import apiClient from './apiClient';
import { buildHipError } from './hipApi';
import { FILE_DISPOSITION, LIST_STATUS } from '../../constants/hipRecordEnums';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * HIP RECORDS API — ABDM M2 HI types (variant B merged OPD + IPD list)
 *
 * Bound to the FROZEN contract in ABDM-M2-HITYPES build-plan.md §4 (4.1-4.4) as amended by
 * the A1 addendum (§4.7: a slot is identified by `slot_key`, not by record type), not to built
 * backend code. Same rules as hipApi.js:
 *   1. Every function returns a camelCase domain object - components never see
 *      axios envelopes or snake_case keys.
 *   2. Errors come from the shared hipApi error builder: err.code (structured
 *      `detail.code`), err.userMessage, err.retryAfter + err.pauseReason (429 LINK_BLOCKED),
 *      err.status. Extras: err.missing (INCOMPLETE_RECORD), err.fileName (400 file errors).
 *   3. No URL construction outside this file.
 *
 * Base: /api/organisation/hip/records
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/hip/records';

// ── Error handling ────────────────────────────────────────────────────

/** Shared HIP error + the contract's extra structured fields. */
function _hipError(error) {
  const err = buildHipError(error);
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    if (Array.isArray(detail.missing)) err.missing = detail.missing;
    if (detail.file_name) err.fileName = detail.file_name;
    if (detail.pause_reason) err.pauseReason = detail.pause_reason; // OR-99: on 429 LINK_BLOCKED, with err.retryAfter
  }
  return err;
}

/** `FileReader` (not `Blob.text`) - jsdom's Blob has no `.text()`. Parsed JSON body, or null. */
function _readBlobJson(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      try { resolve(JSON.parse(reader.result)); } catch (_e) { resolve(null); }
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(blob);
  });
}

// ── Mappers (snake_case contract -> camelCase) ───────────────────────

function _file(f) {
  return {
    fileId: f.file_id,
    fileName: f.file_name,
    contentType: f.content_type,
    sizeBytes: f.size_bytes ?? null,
    uploadedAt: f.uploaded_at ?? null,
  };
}

// Invoice slot only (A1.2); null on every other type.
function _invoice(i) {
  return {
    invoiceId: i.invoice_id,
    billNumber: i.bill_number,
    invoiceType: i.invoice_type,
    totalGrossPaise: i.total_gross_paise,
    status: i.status,
    version: i.version,
  };
}

// An unsaved starting point for an invoice (EncounterRow.invoice_suggestions): the receipt / final
// bill it would be filled in from. Cheap - no lines or amounts; the drawer fetches the prefill on open.
function _suggestion(g) {
  return { sourceKind: g.source_kind, sourceRef: g.source_ref, billNumber: g.bill_number };
}

function _slot(s) {
  return {
    // slotKey is the slot's identity (A1.1): opaque to the FE, never parsed. Several slots can
    // share one recordType (Diagnostic Report, Invoice), so key every map / React key by slotKey.
    slotKey: s.slot_key,
    recordType: s.record_type,
    label: s.label ?? null,
    invoice: s.invoice ? _invoice(s.invoice) : null,
    careContextId: s.care_context_id ?? null,
    source: s.source,
    state: s.state,
    careContextStatus: s.care_context_status ?? null,
    fileCount: s.file_count,
    files: (s.files || []).map(_file),
    replacedByStaff: !!s.replaced_by_staff,
    sourceAt: s.source_at ?? null,
    linkedAt: s.linked_at ?? null,
    canLink: !!s.can_link,
    // OR-55: why the slot cannot be linked (e.g. RX_NOT_APPROVED); null when nothing blocks it.
    blockedReason: s.blocked_reason ?? null,
    canReplace: !!s.can_replace,
    lastLinkAttemptAt: s.last_link_attempt_at ?? null,
    linkError: s.link_error ?? null,
    retryAfter: s.retry_after ?? null,
    // OR-99: on a PAUSED slot, why (ABDM_BLOCKED | RATE_LIMITED); null when the server sends none.
    pauseReason: s.pause_reason ?? null,
  };
}

// OR-75: the patient block of the patient view (only sent with `patient_id`). abhaStatus null = no ABHA on file.
function _patient(p) {
  return {
    patientId: p.patient_id,
    patientName: p.patient_name,
    uhid: p.uhid,
    patientMobileMasked: p.patient_mobile_masked,
    abhaStatus: p.abha_status ?? null,
  };
}

function _record(r) {
  return {
    encounterKind: r.encounter_kind,
    encounterId: r.encounter_id,
    patientId: r.patient_id,
    patientName: r.patient_name,
    uhid: r.uhid,
    patientMobileMasked: r.patient_mobile_masked,
    abhaStatus: r.abha_status,
    encounterAt: r.encounter_at,
    visitDate: r.visit_date ?? null,
    tokenNumber: r.token_number ?? null,
    appointmentNumber: r.appointment_number ?? null,
    ipNumber: r.ip_number ?? null,
    admittedAt: r.admitted_at ?? null,
    dischargedAt: r.discharged_at ?? null,
    doctorName: r.doctor_name,
    // OR-59: the visit has no doctor (LAB walk-in, nurse-only), so every link needs a picked `practitionerId`.
    needsPractitioner: !!r.needs_practitioner,
    departmentName: r.department_name ?? null,
    allowedTypes: r.allowed_types || [],
    slots: (r.slots || []).map(_slot),
    invoiceSuggestions: (r.invoice_suggestions || []).map(_suggestion),
  };
}

// ── 4.1 List ──────────────────────────────────────────────────────────

/**
 * Merged OPD visits + IPD admissions for a date range (max 31 days), newest first, with
 * one slot per record (single types: one per type; Diagnostic Report / Invoice: one per
 * report / invoice, told apart by `slotKey` + `label`) and `invoiceSuggestions` (unsaved invoice
 * starting points: { sourceKind, sourceRef, billNumber }). Server-side filters, counts and pagination.
 *
 * Patient view (A4.3, OR-75): pass `patientId` instead of dates. The server then lists that patient's
 * visits and admissions of the last 12 months (no 31-day cap) and answers with a `patient` block;
 * `dateFrom` / `dateTo` are never sent in that mode.
 *
 * `status` NOT_LINKED_YET (A4.1, OR-86, OR-104, OR-113) = needs linking (a READY, LINKING, LINK_FAILED or PAUSED slot) OR an unsaved invoice
 * suggestion OR ABHA not verified, verified patients first (isNotLinkedYet is the same rule for a verified row).
 *
 * @param {{ dateFrom?: string, dateTo?: string, patientId?: string, kind?: 'ALL'|'OPD'|'IPD',
 *   status?: 'ALL'|'NOT_LINKED_YET'|'NEEDS_LINKING'|'LINK_FAILED'|'NOT_VERIFIED'|'LINKED',
 *   q?: string, page?: number, pageSize?: number }} params
 * @returns {{ records: Array, totalCount: number, counts: { all, opd, ipd, notLinkedYet, needsLinking,
 *   linkFailed, notVerified, linked }, page: number, pageSize: number, dateFrom: string|null,
 *   dateTo: string|null, patient: { patientId, patientName, uhid, patientMobileMasked,
 *   abhaStatus: 'VERIFIED'|'NOT_VERIFIED'|null }|null }}  (`dateFrom`/`dateTo` = the range the server used)
 */
export async function listRecords({
  dateFrom, dateTo, patientId, kind = 'ALL', status = LIST_STATUS.ALL, q, page = 1, pageSize = 20,
}) {
  try {
    const { data } = await apiClient.get(BASE, {
      params: {
        ...(patientId ? { patient_id: patientId } : { date_from: dateFrom, date_to: dateTo }),
        kind,
        status,
        page,
        page_size: pageSize,
        ...(q ? { q } : {}),
      },
    });
    // No `counts` default on purpose: a missing counts block must not pass as real zeros.
    const c = data.counts || {};
    return {
      records: (data.records || []).map(_record),
      totalCount: data.total_count,
      counts: {
        all: c.all,
        opd: c.opd,
        ipd: c.ipd,
        notLinkedYet: c.not_linked_yet,
        needsLinking: c.needs_linking,
        linkFailed: c.link_failed,
        notVerified: c.not_verified,
        linked: c.linked,
      },
      page: data.page,
      pageSize: data.page_size,
      dateFrom: data.date_from ?? null,
      dateTo: data.date_to ?? null,
      patient: data.patient ? _patient(data.patient) : null,
    };
  } catch (err) {
    throw _hipError(err);
  }
}

// ── 4.2 Link ──────────────────────────────────────────────────────────

/**
 * Link one or many slots of ONE encounter (one request = one ABDM link start).
 * Slots are named by `slotKeys` (A1.3) - never by record type: several Diagnostic Reports share one.
 * Invoice slot keys are refused here (TYPE_NOT_ALLOWED) - invoices link through their own route.
 * Per-slot failures come back in `results[].error` (HTTP 200); whole-request failures throw
 * (err.code ENCOUNTER_NOT_FOUND | ABHA_NOT_VERIFIED | RECORD_BEFORE_ABHA_LINK |
 * VISIT_PATIENT_MISMATCH | NOT_DISCHARGED | INCOMPLETE_RECORD (+err.missing) |
 * LINK_BLOCKED (+err.retryAfter) | INVALID_SLOT_KEY | TYPE_NOT_ALLOWED | FIELD_REQUIRED |
 * PRACTITIONER_REQUIRED | PRACTITIONER_INVALID | PRACTITIONER_NOT_ALLOWED |
 * NO_DOCTORS_IN_ORGANISATION (OR-59, 422)).
 * `practitionerId` (OR-59) is the doctor staff picked for a visit with no doctor
 * (`EncounterRow.needsPractitioner`): sent only when given, saved only on what goes to ABHA.
 *
 * @param {{ encounterKind: 'OPD'|'IPD', encounterId: string, slotKeys: string[], practitionerId?: string }} params
 * @returns {{ results: Array<{ slotKey, recordType, ok, careContextId, state, error: {code, message}|null }> }}
 */
export async function linkRecords({ encounterKind, encounterId, slotKeys, practitionerId }) {
  try {
    const { data } = await apiClient.post(`${BASE}/link`, {
      encounter_kind: encounterKind,
      encounter_id: encounterId,
      slot_keys: slotKeys,
      ...(practitionerId ? { practitioner_id: practitionerId } : {}),
    });
    return {
      results: (data.results || []).map((r) => ({
        slotKey: r.slot_key,
        recordType: r.record_type,
        ok: !!r.ok,
        careContextId: r.care_context_id ?? null,
        state: r.state ?? null,
        error: r.error ? { code: r.error.code, message: r.error.message } : null,
      })),
    };
  } catch (err) {
    throw _hipError(err);
  }
}

// ── 4.3 Upload / Replace ──────────────────────────────────────────────

/**
 * Upload (mode ADD) or Replace (mode REPLACE) files on one slot. Never links.
 * `slotKey` (A1.4) names the slot to REPLACE: required for Diagnostic Report (SLOT_KEY_REQUIRED),
 * optional for single types. An ADD of a Diagnostic Report always creates a NEW slot, so it has
 * no slotKey. Sent only when given.
 * Errors: err.code FILE_TYPE_NOT_ALLOWED | FILE_EMPTY | FILE_TOO_LARGE | FILE_REJECTED
 * (+err.fileName) | TYPE_NOT_ALLOWED | UNKNOWN_FIELD | NOTHING_TO_REPLACE | TOO_MANY_FILES |
 * RECORD_TOO_LARGE | SLOT_LOCKED | ENCOUNTER_NOT_FOUND | SLOT_KEY_REQUIRED | INVALID_SLOT_KEY.
 *
 * @param {{ encounterKind: 'OPD'|'IPD', encounterId: string, recordType: string, slotKey?: string,
 *   mode: 'ADD'|'REPLACE', title?: string, files: File[] }} params
 * @returns {{ slot }}
 */
export async function uploadRecordFiles({ encounterKind, encounterId, recordType, slotKey, mode, title, files }) {
  try {
    const form = new FormData();
    form.append('encounter_kind', encounterKind);
    form.append('encounter_id', encounterId);
    form.append('record_type', recordType);
    if (slotKey) form.append('slot_key', slotKey);
    form.append('mode', mode);
    if (title) form.append('title', title);
    files.forEach((f) => form.append('files', f));
    const { data } = await apiClient.post(`${BASE}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { slot: _slot(data.slot) };
  } catch (err) {
    throw _hipError(err);
  }
}

// ── 4.4 View / Download ───────────────────────────────────────────────

/** `filename="x.pdf"` (or unquoted) from a Content-Disposition value; null if absent. */
function _fileNameFromDisposition(header) {
  const m = /filename="?([^";]+)"?/i.exec(header || '');
  return m ? m[1] : null;
}

/**
 * Bytes of one file of a slot (the Linked copy once LINKING/LINKED), or - with
 * `fileId: FILE_ID_ALL` (OR-34) - ONE zip of every file of a multi-file slot (a single-file slot
 * comes back as the file itself).
 * Name the slot by `slotKey` (A1.5; required for Diagnostic Report). `recordType` is still
 * accepted for single types; if both are sent they must agree (else 422 INVALID_SLOT_KEY).
 * `fileName` is read from Content-Disposition, which the browser only exposes cross-origin
 * if the server lists it in Access-Control-Expose-Headers - so it can be null; callers fall
 * back to the slot file's own `fileName`.
 *
 * @param {{ encounterKind, encounterId, slotKey?: string, recordType?: string,
 *   fileId: string, disposition?: 'inline'|'attachment' }} params
 * @returns {{ blob: Blob, fileName: string|null }}
 */
export async function fetchRecordFile({
  encounterKind, encounterId, slotKey, recordType, fileId, disposition = FILE_DISPOSITION.INLINE,
}) {
  try {
    const res = await apiClient.get(`${BASE}/file`, {
      params: {
        encounter_kind: encounterKind,
        encounter_id: encounterId,
        ...(recordType ? { record_type: recordType } : {}),
        ...(slotKey ? { slot_key: slotKey } : {}),
        file_id: fileId,
        disposition,
      },
      responseType: 'blob',
    });
    return { blob: res.data, fileName: _fileNameFromDisposition(res.headers?.['content-disposition']) };
  } catch (error) {
    // responseType 'blob' makes axios deliver the error body as a Blob; decode it so
    // err.code (FILE_NOT_FOUND, dept-wall 403) survives.
    if (error?.response?.data instanceof Blob) {
      const body = await _readBlobJson(error.response.data);
      if (body) throw _hipError({ ...error, response: { ...error.response, data: body } });
    }
    throw _hipError(error);
  }
}
